import { addDays, format, parseISO } from 'date-fns';
import { Transaction } from '@/types/transaction';
import { analyzeSpendingPattern, SpendingPattern } from './time-series';

export interface BudgetPrediction {
  predictedSpend: number;
  confidence: number;
  dailyProjections: Array<{
    date: string;
    predictedAmount: number;
    lowerBound: number;
    upperBound: number;
  }>;
  riskFactors: Array<{
    type: 'seasonal' | 'trend' | 'volatility' | 'recent_spike' | 'budget_risk';
    impact: 'low' | 'medium' | 'high';
    description: string;
  }>;
  pattern: SpendingPattern;
}

export async function predictBudgetSpend(
  transactions: Transaction[],
  categoryId: string,
  daysToPredict: number = 30,
  currentBudget?: number
): Promise<BudgetPrediction> {
  // Get spending patterns
  const pattern = analyzeSpendingPattern(transactions, categoryId);
  
  // Calculate base prediction using weighted moving average
  const dailyProjections = calculateDailyProjections(pattern, daysToPredict);
  
  // Calculate confidence based on data quality and volatility
  const confidence = calculateConfidence(pattern, transactions.length);
  
  // Identify risk factors
  const riskFactors = identifyRiskFactors(pattern, currentBudget);
  
  // Calculate total predicted spend
  const predictedSpend = dailyProjections.reduce(
    (sum, day) => sum + day.predictedAmount, 
    0
  );
  
  return {
    predictedSpend,
    confidence,
    dailyProjections,
    riskFactors,
    pattern
  };
}

function calculateDailyProjections(
  pattern: SpendingPattern,
  days: number
) {
  const projections = [];
  const today = new Date();
  
  // Calculate base daily average (weighted more heavily to recent data)
  const dailyValues = Object.entries(pattern.dailyAverages)
    .sort(([dateA], [dateB]) => dateB.localeCompare(dateA)) // Most recent first
    .map(([_, value]) => value);
  
  // Weight recent days more heavily
  let weightedSum = 0;
  let weightSum = 0;
  dailyValues.forEach((value, i) => {
    const weight = Math.pow(0.9, i); // Exponential decay
    weightedSum += value * weight;
    weightSum += weight;
  });
  
  const baseDaily = weightSum > 0 ? weightedSum / weightSum : 0;
  
  // Generate projections for each day
  for (let i = 1; i <= days; i++) {
    const date = addDays(today, i);
    const dayOfWeek = date.getDay();
    const weeklyFactor = (pattern.weeklyPattern[dayOfWeek] || 0) / 100;
    
    // Apply trend (convert monthly trend to daily)
    const trendFactor = (pattern.monthlyTrend / 30) * i;
    const predictedAmount = baseDaily * (1 + trendFactor) * (1 + weeklyFactor);
    
    // Add some randomness based on historical volatility
    const volatilityFactor = pattern.volatility / 100;
    const randomFactor = 1 + (Math.random() * 2 - 1) * volatilityFactor;
    
    projections.push({
      date: date.toISOString(),
      predictedAmount: Math.max(0, predictedAmount * randomFactor),
      lowerBound: Math.max(0, predictedAmount * (1 - volatilityFactor * 0.8)),
      upperBound: predictedAmount * (1 + volatilityFactor * 0.8)
    });
  }
  
  return projections;
}

function calculateConfidence(pattern: SpendingPattern, sampleSize: number): number {
  // Base confidence on data quality and volatility
  const volatilityFactor = Math.max(0, 1 - (pattern.volatility / 100));
  const sampleFactor = Math.min(1, sampleSize / 30); // More data = higher confidence
  const trendFactor = Math.max(0.5, 1 - (Math.abs(pattern.monthlyTrend) / 100));
  
  // Weighted average of factors
  return Math.min(100, ((volatilityFactor * 0.4) + (sampleFactor * 0.3) + (trendFactor * 0.3)) * 100);
}

function identifyRiskFactors(
  pattern: SpendingPattern,
  currentBudget?: number
): BudgetPrediction['riskFactors'] {
  const risks: BudgetPrediction['riskFactors'] = [];
  
  // High volatility risk
  if (pattern.volatility > 50) {
    risks.push({
      type: 'volatility',
      impact: 'high',
      description: 'High spending volatility detected in this category'
    });
  } else if (pattern.volatility > 20) {
    risks.push({
      type: 'volatility',
      impact: 'medium',
      description: 'Moderate spending volatility in this category'
    });
  }
  
  // Upward trend risk
  if (pattern.monthlyTrend > 10) {
    risks.push({
      type: 'trend',
      impact: 'high',
      description: `Spending trending up by ${pattern.monthlyTrend.toFixed(1)}% monthly`
    });
  } else if (pattern.monthlyTrend > 5) {
    risks.push({
      type: 'trend',
      impact: 'medium',
      description: `Spending trending up by ${pattern.monthlyTrend.toFixed(1)}% monthly`
    });
  }
  
  // Budget risk
  if (currentBudget !== undefined) {
    const monthlySpend = Object.values(pattern.dailyAverages).reduce(
      (sum, val) => sum + val, 0
    );
    const projectedMonthly = monthlySpend * (1 + pattern.monthlyTrend / 100);
    
    if (projectedMonthly > currentBudget * 1.2) {
      risks.push({
        type: 'budget_risk',
        impact: 'high',
        description: `Projected spending ($${projectedMonthly.toFixed(2)}) exceeds budget by ${((projectedMonthly / currentBudget - 1) * 100).toFixed(0)}%`
      });
    } else if (projectedMonthly > currentBudget) {
      risks.push({
        type: 'budget_risk',
        impact: 'medium',
        description: `Projected spending ($${projectedMonthly.toFixed(2)}) is close to budget limit`
      });
    }
  }
  
  return risks;
}
